import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * Module-level store of the currently mounted Folio panel element.
 *
 * The panel publishes its element via `setFolioPanelElement(node)` from a
 * callback ref; consumers subscribe through `useFolioPanelWidth`. Avoids
 * any DOM lookup, global event bus, or whole-tree mutation observation.
 */
let folioPanelElement: HTMLElement | null = null;
const folioPanelListeners = new Set<() => void>();

function emitFolioPanelChange(): void {
  for (const listener of folioPanelListeners) {
    listener();
  }
}

export function setFolioPanelElement(next: HTMLElement | null): void {
  if (folioPanelElement === next) return;
  folioPanelElement = next;
  emitFolioPanelChange();
}

function subscribeFolioPanelElement(listener: () => void): () => void {
  folioPanelListeners.add(listener);
  return () => {
    folioPanelListeners.delete(listener);
  };
}

function getFolioPanelElement(): HTMLElement | null {
  return folioPanelElement;
}

function getFolioPanelElementServer(): null {
  return null;
}

/**
 * Tracks the rendered width of the Folio panel so overlay sidebars can open
 * beside it. Returns `0` when the panel is not currently mounted.
 */
export function useFolioPanelWidth(): number {
  const element = useSyncExternalStore(
    subscribeFolioPanelElement,
    getFolioPanelElement,
    getFolioPanelElementServer,
  );
  const [width, setWidth] = useState(0);

  useEffect(() => {
    if (element === null) {
      setWidth(0);
      return;
    }
    setWidth(element.offsetWidth);
    const resizeObserver = new ResizeObserver(() => {
      setWidth(element.offsetWidth);
    });
    resizeObserver.observe(element);
    return () => {
      resizeObserver.disconnect();
    };
  }, [element]);

  return width;
}
